/**
 * Computes the free-text field changes between two draft plans, so the
 * conversation can show a before/after (strikethrough) diff after each revision.
 *
 * The diff is DETERMINISTIC: it compares plan snapshots taken before and after
 * the turn, and never consults what the agent claims it changed.
 *
 * Items are matched by stable `id` when both sides carry one, falling back to
 * the display identifier (attribute `reference`, table `name`, column
 * name/`attribute_reference`) for drafts created before ids existed. Matching
 * by id means a rename is reported as a name change rather than hiding every
 * other change on the renamed item. Items present in only one plan are
 * reported as single "Added"/"Removed" entries rather than per-field diffs,
 * so a deletion reads as one chip and a wording edit never reads as
 * add+remove.
 */
import {
  attributeTestColumnKey,
  withRemovedInPlace,
} from "../../../../../components/audit/collab/planGhosts";
import { collabFieldKeys } from "../../../../../components/audit/collab/planFieldKeys";
// Re-exported so this module stays the plan-diff import surface.
export { attributeTestColumnKey, withRemovedInPlace };
export type { GhostedEntry } from "../../../../../components/audit/collab/planGhosts";
import { FieldDiff } from "../../../../../models/audit/collabFieldDiff";
import {
  DraftAnalyticsColumn,
  DraftAttributeTestColumn,
  DraftPlan,
  DraftSupportColumn,
  DraftTestingTable,
  ProposedAttribute,
} from "../../../../../models/audit/collabControlSession";

// Re-exported so this module stays the plan-diff import surface; the diff
// item itself names no entity — see models/audit/collabFieldDiff.
export type { FieldDiff };

/**
 * Canonical diff-key builders, shared by the differ (below) and the plan
 * panel's field render sites, so a field can look up its own diff and chat
 * links can address it. Keys embed the item's CURRENT (post-revision) name so
 * they resolve against what the panel renders. Any change to a key shape must
 * go through here.
 */
export const fieldKeys = {
  ...collabFieldKeys,
  controlName: () => "control_name",
  controlDescription: () => "control_description",
  keyReport: (name: string) => `keyreport:${name}`,
};

export type PlanTab = "structure" | "instructions";

/** Which plan-panel tab a diffed field lives on, derived from its key. */
export function tabForFieldKey(key: string): PlanTab {
  if (
    key.startsWith("col:") ||
    key.startsWith("support:") ||
    key.startsWith("analytics:")
  ) {
    return "instructions";
  }
  if (
    key.startsWith("table:") &&
    (key.endsWith(":tc") || key.endsWith(":ds"))
  ) {
    return "instructions";
  }
  return "structure";
}

const norm = (value: string | null | undefined): string => value ?? "";

/** Stable identity for an attribute-test column within its table.

Falls back to the id when both display fields are null so two unnamed columns
never collide on "" — colliding keys would pair them as one item in matching
and merge their field diffs. */
/**
 * Pairs items across the two plans: by `id` when both sides have one, by the
 * display key otherwise. `pairs` holds items present in both plans; `removed`
 * and `added` hold the leftovers, so deletions and additions get reported
 * instead of silently dropping out of the diff.
 */
function matchItems<T>(
  prev: readonly T[],
  next: readonly T[],
  displayKey: (item: T) => string,
  id: (item: T) => string | null | undefined,
): { pairs: [T, T][]; removed: T[]; added: T[] } {
  const prevById = new Map<string, T>();
  const prevByKey = new Map<string, T>();
  for (const item of prev) {
    const itemId = id(item);
    if (itemId) prevById.set(itemId, item);
    prevByKey.set(displayKey(item), item);
  }

  const pairs: [T, T][] = [];
  const added: T[] = [];
  const matchedPrev = new Set<T>();
  for (const item of next) {
    const itemId = id(item);
    const match =
      (itemId ? prevById.get(itemId) : undefined) ??
      prevByKey.get(displayKey(item));
    // A prev item pairs at most once: an item already claimed by an id match
    // (e.g. matched under a new name) must not also display-key-match a
    // genuinely new item — that would hide the addition.
    if (match && !matchedPrev.has(match)) {
      pairs.push([match, item]);
      matchedPrev.add(match);
    } else {
      added.push(item);
    }
  }
  const removed = prev.filter((item) => !matchedPrev.has(item));
  return { pairs, removed, added };
}

/** An item the latest revision removed, with its index in the previous list. */
export interface RemovedItem<T> {
  item: T;
  index: number;
}

/** Everything the latest revision removed, grouped for in-place ghost rendering. */
export interface RemovedPlanItems {
  attributes: RemovedItem<ProposedAttribute>[];
  tables: RemovedItem<DraftTestingTable>[];
  /** Keyed by the CURRENT (next-plan) table name. */
  testColumns: Map<string, RemovedItem<DraftAttributeTestColumn>[]>;
  /** Keyed by the CURRENT (next-plan) table name. */
  supportColumns: Map<string, RemovedItem<DraftSupportColumn>[]>;
  /**
   * Keyed by the CURRENT (next-plan) table name. Leaves out a saved column the
   * next plan records in `removedAnalyticsColumns`: that one already renders
   * as its table's removed row, under the same field key.
   */
  analyticsColumns: Map<string, RemovedItem<DraftAnalyticsColumn>[]>;
}

/**
 * Collects the items present in `prev` but not `next`, with their original
 * positions, so the panel can render each ghost exactly where the item lived.
 */
export function computeRemovedPlanItems(
  prev: DraftPlan,
  next: DraftPlan,
): RemovedPlanItems {
  const withIndex = <T>(removed: T[], source: readonly T[]): RemovedItem<T>[] =>
    removed.map((item) => ({ item, index: source.indexOf(item) }));

  const attributeMatch = matchItems(
    prev.proposedAttributes,
    next.proposedAttributes,
    (a) => a.reference,
    (a) => a.id,
  );
  const tableMatch = matchItems(
    prev.testingTables,
    next.testingTables,
    (t) => t.name,
    (t) => t.id,
  );

  const testColumns = new Map<
    string,
    RemovedItem<DraftAttributeTestColumn>[]
  >();
  const supportColumns = new Map<string, RemovedItem<DraftSupportColumn>[]>();
  const analyticsColumns = new Map<
    string,
    RemovedItem<DraftAnalyticsColumn>[]
  >();
  for (const [before, table] of tableMatch.pairs) {
    const columnMatch = matchItems(
      before.attributeTestColumns,
      table.attributeTestColumns,
      attributeTestColumnKey,
      (c) => c.id,
    );
    if (columnMatch.removed.length > 0) {
      testColumns.set(
        table.name,
        withIndex(columnMatch.removed, before.attributeTestColumns),
      );
    }
    const supportMatch = matchItems(
      before.supportColumns,
      table.supportColumns,
      (c) => c.name,
      (c) => c.id,
    );
    if (supportMatch.removed.length > 0) {
      supportColumns.set(
        table.name,
        withIndex(supportMatch.removed, before.supportColumns),
      );
    }
    const analyticsMatch = matchItems(
      before.analyticsColumns,
      table.analyticsColumns,
      (c) => c.name,
      (c) => c.id,
    );
    const recordedRemovalIds = new Set(
      table.removedAnalyticsColumns.map((c) => c.id),
    );
    const droppedAnalytics = analyticsMatch.removed.filter(
      (c) => !c.id || !recordedRemovalIds.has(c.id),
    );
    if (droppedAnalytics.length > 0) {
      analyticsColumns.set(
        table.name,
        withIndex(droppedAnalytics, before.analyticsColumns),
      );
    }
  }

  return {
    attributes: withIndex(attributeMatch.removed, prev.proposedAttributes),
    tables: withIndex(tableMatch.removed, prev.testingTables),
    testColumns,
    supportColumns,
    analyticsColumns,
  };
}

function pushIfChanged(
  out: FieldDiff[],
  key: string,
  label: string,
  before: string | null | undefined,
  after: string | null | undefined,
): void {
  const b = norm(before);
  const a = norm(after);
  if (b !== a && (b !== "" || a !== "")) {
    out.push({ key, label, before: b, after: a });
  }
}

export function diffPlanFreeText(
  prev: DraftPlan,
  next: DraftPlan,
): FieldDiff[] {
  const diffs: FieldDiff[] = [];

  pushIfChanged(
    diffs,
    fieldKeys.controlName(),
    "Name",
    prev.controlName,
    next.controlName,
  );
  pushIfChanged(
    diffs,
    fieldKeys.controlDescription(),
    "Description",
    prev.controlDescription,
    next.controlDescription,
  );

  // Attributes matched by id, falling back to reference.
  const attributeMatch = matchItems(
    prev.proposedAttributes,
    next.proposedAttributes,
    (a) => a.reference,
    (a) => a.id,
  );
  for (const [before, attr] of attributeMatch.pairs) {
    // A re-lettered code (attributes match by id, so the reference itself can change)
    // registers its own chip. Distinct key so it doesn't collide with the statement chip,
    // but it navigates to the same attribute card.
    if (norm(before.reference) !== norm(attr.reference)) {
      diffs.push({
        key: `${fieldKeys.attribute(attr.reference)}:code`,
        navigateKey: fieldKeys.attribute(attr.reference),
        label: `Attribute ${attr.reference} — Code`,
        before: before.reference,
        after: attr.reference,
      });
    }
    pushIfChanged(
      diffs,
      fieldKeys.attribute(attr.reference),
      `Attribute ${attr.reference}`,
      before.statement,
      attr.statement,
    );
  }
  for (const attr of attributeMatch.removed) {
    diffs.push({
      key: fieldKeys.attribute(attr.reference),
      label: `Attribute ${attr.reference} — Removed`,
      before: attr.statement,
      after: "",
      change: "removed",
      itemName: attr.reference,
    });
  }
  for (const attr of attributeMatch.added) {
    diffs.push({
      key: fieldKeys.attribute(attr.reference),
      label: `Attribute ${attr.reference} — Added`,
      before: "",
      after: attr.statement,
      change: "added",
      itemName: attr.reference,
    });
  }

  // Testing tables matched by id, falling back to name.
  const tableMatch = matchItems(
    prev.testingTables,
    next.testingTables,
    (t) => t.name,
    (t) => t.id,
  );
  for (const table of tableMatch.removed) {
    diffs.push({
      key: fieldKeys.tableName(table.name),
      label: `${table.name} — Table Removed`,
      before: table.purpose,
      after: "",
      change: "removed",
      itemName: table.name,
    });
  }
  for (const table of tableMatch.added) {
    diffs.push({
      key: fieldKeys.tableName(table.name),
      label: `${table.name} — Table Added`,
      before: "",
      after: table.purpose,
      change: "added",
      itemName: table.name,
    });
  }
  for (const [before, table] of tableMatch.pairs) {
    pushIfChanged(
      diffs,
      fieldKeys.tableName(table.name),
      `${table.name} — Table Name`,
      before.name,
      table.name,
    );
    pushIfChanged(
      diffs,
      fieldKeys.tablePurpose(table.name),
      `${table.name} — Purpose`,
      before.purpose,
      table.purpose,
    );
    pushIfChanged(
      diffs,
      fieldKeys.tableTestingContext(table.name),
      `${table.name} — Testing Context`,
      before.testingContext,
      table.testingContext,
    );
    pushIfChanged(
      diffs,
      fieldKeys.tableDocStandards(table.name),
      `${table.name} — Documentation Standards`,
      before.documentationStandards,
      table.documentationStandards,
    );
    pushIfChanged(
      diffs,
      fieldKeys.tableAttributeRefs(table.name),
      `${table.name} — Attribute References`,
      before.attributeReferences.join(", "),
      table.attributeReferences.join(", "),
    );

    // Attribute test columns matched by id, falling back to name/reference.
    const columnMatch = matchItems(
      before.attributeTestColumns,
      table.attributeTestColumns,
      attributeTestColumnKey,
      (c) => c.id,
    );
    for (const col of columnMatch.removed) {
      const label = col.name ?? col.attributeReference ?? "Test column";
      diffs.push({
        key: fieldKeys.colName(table.name, attributeTestColumnKey(col)),
        label: `${label} — Column Removed`,
        before: col.passCriteria,
        after: "",
        change: "removed",
        itemName: label,
        tableName: table.name,
      });
    }
    for (const col of columnMatch.added) {
      const label = col.name ?? col.attributeReference ?? "Test column";
      diffs.push({
        key: fieldKeys.colName(table.name, attributeTestColumnKey(col)),
        label: `${label} — Column Added`,
        before: "",
        after: col.passCriteria,
        change: "added",
        itemName: label,
        tableName: table.name,
      });
    }
    for (const [bc, col] of columnMatch.pairs) {
      const label = col.name ?? col.attributeReference ?? "Test column";
      const key = attributeTestColumnKey(col);
      pushIfChanged(
        diffs,
        fieldKeys.colName(table.name, key),
        `${label} — Name`,
        bc.name,
        col.name,
      );
      pushIfChanged(
        diffs,
        fieldKeys.colPass(table.name, key),
        `${label} — Pass`,
        bc.passCriteria,
        col.passCriteria,
      );
      pushIfChanged(
        diffs,
        fieldKeys.colFail(table.name, key),
        `${label} — Fail`,
        bc.failCriteria,
        col.failCriteria,
      );
      pushIfChanged(
        diffs,
        fieldKeys.colNa(table.name, key),
        `${label} — N/A`,
        bc.naCriteria,
        col.naCriteria,
      );
      pushIfChanged(
        diffs,
        fieldKeys.colGuidance(table.name, key),
        `${label} — Procedure Guidance`,
        bc.procedureGuidance,
        col.procedureGuidance,
      );
      pushIfChanged(
        diffs,
        fieldKeys.colTolerances(table.name, key),
        `${label} — Tolerances`,
        bc.tolerances,
        col.tolerances,
      );
      pushIfChanged(
        diffs,
        fieldKeys.colExpectedEvidences(table.name, key),
        `${label} — Expected Evidence`,
        bc.expectedEvidences,
        col.expectedEvidences,
      );
    }

    // Support columns matched by id, falling back to name.
    const supportMatch = matchItems(
      before.supportColumns,
      table.supportColumns,
      (c) => c.name,
      (c) => c.id,
    );
    for (const col of supportMatch.removed) {
      diffs.push({
        key: fieldKeys.supportName(table.name, col.name),
        label: `${col.name} — Column Removed`,
        before: col.description,
        after: "",
        change: "removed",
        itemName: col.name,
        tableName: table.name,
      });
    }
    for (const col of supportMatch.added) {
      diffs.push({
        key: fieldKeys.supportName(table.name, col.name),
        label: `${col.name} — Column Added`,
        before: "",
        after: col.description,
        change: "added",
        itemName: col.name,
        tableName: table.name,
      });
    }
    for (const [bc, col] of supportMatch.pairs) {
      pushIfChanged(
        diffs,
        fieldKeys.supportName(table.name, col.name),
        `${col.name} — Name`,
        bc.name,
        col.name,
      );
      pushIfChanged(
        diffs,
        fieldKeys.supportDescription(table.name, col.name),
        `${col.name} — Description`,
        bc.description,
        col.description,
      );
    }

    // Analytics columns matched by id, falling back to name.
    const analyticsMatch = matchItems(
      before.analyticsColumns,
      table.analyticsColumns,
      (c) => c.name,
      (c) => c.id,
    );
    for (const col of analyticsMatch.removed) {
      diffs.push({
        key: fieldKeys.analyticsName(table.name, col.name),
        label: `${col.name} — Column Removed`,
        before: col.passCriteria,
        after: "",
        change: "removed",
        itemName: col.name,
        tableName: table.name,
      });
    }
    for (const col of analyticsMatch.added) {
      diffs.push({
        key: fieldKeys.analyticsName(table.name, col.name),
        label: `${col.name} — Column Added`,
        before: "",
        after: col.passCriteria,
        change: "added",
        itemName: col.name,
        tableName: table.name,
      });
    }
    for (const [bc, col] of analyticsMatch.pairs) {
      pushIfChanged(
        diffs,
        fieldKeys.analyticsName(table.name, col.name),
        `${col.name} — Name`,
        bc.name,
        col.name,
      );
      pushIfChanged(
        diffs,
        fieldKeys.analyticsPass(table.name, col.name),
        `${col.name} — Pass`,
        bc.passCriteria,
        col.passCriteria,
      );
      pushIfChanged(
        diffs,
        fieldKeys.analyticsFail(table.name, col.name),
        `${col.name} — Fail`,
        bc.failCriteria,
        col.failCriteria,
      );
      pushIfChanged(
        diffs,
        fieldKeys.analyticsNa(table.name, col.name),
        `${col.name} — N/A`,
        bc.naCriteria,
        col.naCriteria,
      );
      pushIfChanged(
        diffs,
        fieldKeys.analyticsGuidance(table.name, col.name),
        `${col.name} — Procedure Guidance`,
        bc.procedureGuidance,
        col.procedureGuidance,
      );
      pushIfChanged(
        diffs,
        fieldKeys.analyticsTolerances(table.name, col.name),
        `${col.name} — Tolerances`,
        bc.tolerances,
        col.tolerances,
      );
      pushIfChanged(
        diffs,
        fieldKeys.analyticsExpectedEvidences(table.name, col.name),
        `${col.name} — Expected Evidence`,
        bc.expectedEvidences,
        col.expectedEvidences,
      );
      pushIfChanged(
        diffs,
        fieldKeys.analyticsDraftedTest(table.name, col.name),
        `${col.name} — Drafted Test`,
        bc.draftedTest,
        col.draftedTest,
      );
    }
  }

  // Key reports — matched by name (no id on the wire shape).
  const keyReportMatch = matchItems(
    prev.keyReports ?? [],
    next.keyReports ?? [],
    (r) => r.name,
    () => null,
  );
  for (const [before, report] of keyReportMatch.pairs) {
    pushIfChanged(
      diffs,
      fieldKeys.keyReport(report.name),
      `Key report ${report.name}`,
      before.description,
      report.description,
    );
  }
  for (const report of keyReportMatch.removed) {
    diffs.push({
      key: fieldKeys.keyReport(report.name),
      label: `Key report ${report.name} — Removed`,
      before: report.description,
      after: "",
      change: "removed",
      itemName: report.name,
    });
  }
  for (const report of keyReportMatch.added) {
    diffs.push({
      key: fieldKeys.keyReport(report.name),
      label: `Key report ${report.name} — Added`,
      before: "",
      after: report.description,
      change: "added",
      itemName: report.name,
    });
  }

  return diffs;
}
